import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { MODULES, type ModuleKey } from "@kipo/shared";
import { MODULE_ICON, type IconName } from "../ui/Icon";
import { Spinner } from "../ui/kit";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "../ui/command";
import { useTRPC } from "../lib/trpc";
import { moduleColor, useMe } from "../lib/me";
import { canOpen, moduleHome } from "../lib/access";
import { THEME_OPTIONS, setThemePref, useThemePref } from "../lib/theme";

const TYPE_ICON: Record<string, IconName> = { company: "building", contact: "user", deal: "kanban", project: "kanban", task: "check-list", invoice: "receipt", quote: "file-text" };
const TYPE_GROUP: Record<string, string> = { project: "Progetti", task: "Task", company: "Aziende", contact: "Contatti", deal: "Deal", quote: "Preventivi", invoice: "Fatture" };
/** Scorciatoie "G + lettera" per aprire la panoramica di ogni modulo. */
const MODULE_SHORTCUT: Partial<Record<ModuleKey, string>> = { projects: "G P", crm: "G C", sales: "G V", contracts: "G O", expenses: "G S", marketing: "G M" };

type Page = "projects" | "theme";
/** entro quanto una riapertura riprende dalla sottopagina lasciata */
const REOPEN_MS = 5000;
const PAGES: Record<Page, { label: string; placeholder: string }> = {
  projects: { label: "Progetti", placeholder: "Cerca un progetto…" },
  theme: { label: "Tema", placeholder: "Scegli il tema…" },
};

/** Una voce della palette: va a una pagina (`to`), fa un'azione (`run`) o apre una sottopagina (`page`). */
interface Entry {
  value: string;
  group: string;
  label: string;
  icon: IconName;
  color?: string;
  to?: string;
  search?: Record<string, unknown>;
  run?: () => void;
  page?: Page;
  shortcut?: string;
  keywords?: string;
}

function useDebounced<T>(v: T, ms = 180) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

const matches = (e: Entry, q: string) => !q || `${e.label} ${e.group} ${e.keywords ?? ""}`.toLowerCase().includes(q.toLowerCase());

/** Ricerca globale ⌘K: azioni per categoria, risultati da tutti i moduli, sottopagine e scorciatoie "G + lettera". */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const me = useMe().data;
  const theme = useThemePref();
  const [q, setQ] = useState("");
  const [pages, setPages] = useState<Page[]>([]);
  const page = pages.at(-1);
  const input = useRef<HTMLInputElement>(null);
  const dq = useDebounced(q.trim());
  const search = useQuery({ ...trpc.core.search.queryOptions({ q: dq, limit: 8 }), enabled: open && !page && dq.length >= 2 });
  const projects = useQuery({ ...trpc.projects.list.queryOptions({ status: "ACTIVE" }), enabled: open && page === "projects" });

  // riaperta entro pochi secondi, la palette torna alla sottopagina in cui era (es. "Progetti")
  const lastClose = useRef<{ at: number; pages: Page[] } | null>(null);
  useEffect(() => {
    if (open) {
      const last = lastClose.current;
      setQ("");
      setPages(last && Date.now() - last.at < REOPEN_MS ? last.pages : []);
      setTimeout(() => input.current?.focus(), 30);
    } else lastClose.current = { at: Date.now(), pages };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // tutte le voci della radice, già filtrate per moduli attivi e permessi
  const entries = useMemo(() => {
    if (!me) return [];
    const can = (p: string) => me.permissions.includes(p as never);
    const active = me.entitlement.activeModules;
    const out: Entry[] = [
      { value: "go-home", group: "Generale", label: "Panoramica", icon: "home", to: "/", shortcut: "G H", keywords: "home dashboard" },
      ...(me.role !== "client" ? [{ value: "go-today", group: "Generale", label: "Le mie attività", icon: "check-list", to: "/today", shortcut: "G A", keywords: "oggi scadenze todo" } as Entry] : []),
      { value: "go-notifications", group: "Generale", label: "Notifiche", icon: "bell", to: "/notifications", shortcut: "G N" },
      ...(can("documents:read") ? [{ value: "go-documents", group: "Generale", label: "Documenti", icon: "folder", to: "/documents", shortcut: "G D", keywords: "drive file" } as Entry] : []),
      ...(active.includes("ai") && can("ai:use") ? [{ value: "go-ai", group: "Generale", label: "Kipo AI", icon: "sparkles", to: "/assistant", shortcut: "G I", keywords: "assistente chat" } as Entry] : []),
    ];
    for (const k of ["projects", "crm", "sales", "contracts", "expenses", "marketing"] as const) {
      const m = MODULES[k];
      if (!active.includes(k) || !can(m.entryPermission)) continue;
      const home = moduleHome(k, me);
      out.push({ value: `${k}-home`, group: m.name, label: `Panoramica ${m.name}`, icon: MODULE_ICON[k] ?? "chart", color: m.color, to: home, shortcut: MODULE_SHORTCUT[k] });
      if (k === "projects" && can("projects:write")) out.push({ value: "projects-new", group: m.name, label: "Nuovo progetto", icon: "plus", color: m.color, to: "/projects", search: { new: 1 }, keywords: "crea" });
      if (k === "projects") out.push({ value: "projects-open", group: m.name, label: "Apri un progetto…", icon: "search", color: m.color, page: "projects" });
      if (k === "sales" && can("quotes:write")) out.push({ value: "sales-new-quote", group: m.name, label: "Nuovo preventivo", icon: "plus", color: m.color, to: "/sales/quotes/new", keywords: "crea offerta" });
      for (const n of m.navigation) if (n.to !== home && canOpen(n.to, me, k)) out.push({ value: `${k}-${n.key}`, group: m.name, label: n.label, icon: n.icon, color: m.color, to: n.to });
    }
    out.push(
      { value: "settings-profile", group: "Impostazioni", label: "Profilo", icon: "user", to: "/settings/profile", keywords: "account password" },
      { value: "settings-notifications", group: "Impostazioni", label: "Notifiche email", icon: "mail", to: "/settings/notifications" },
      { value: "settings-mcp", group: "Impostazioni", label: "Collega un agente AI", icon: "sparkles", to: "/settings/mcp", keywords: "mcp claude chatgpt cursor chiave api" },
      { value: "settings-theme", group: "Impostazioni", label: "Cambia tema…", icon: "sun", page: "theme", keywords: "scuro chiaro dark light" },
    );
    return out;
  }, [me]);

  // scorciatoie "G + lettera" quando la palette è chiusa e non si sta scrivendo
  useEffect(() => {
    let armed = 0;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (open || e.metaKey || e.ctrlKey || e.altKey || t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      const key = e.key.toUpperCase();
      if (armed && Date.now() - armed < 1000) {
        armed = 0;
        const hit = entries.find((x) => x.shortcut === `G ${key}`);
        if (hit?.to) (e.preventDefault(), navigate({ to: hit.to, search: hit.search as never }));
        return;
      }
      if (key === "G") armed = Date.now();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entries, open, navigate]);

  const select = (e: Entry) => {
    if (e.page) return (setPages((p) => [...p, e.page!]), setQ(""));
    onClose();
    if (e.run) e.run();
    else if (e.to) navigate({ to: e.to, search: e.search as never });
  };
  const go = (link: string) => {
    onClose();
    const [to, qs] = link.split("?");
    navigate({ to: to!, search: Object.fromEntries(new URLSearchParams(qs ?? "")) as never });
  };

  const needle = page ? q.trim() : dq;
  const groups = [...new Set(entries.map((e) => e.group))].map((g) => ({ g, items: entries.filter((e) => e.group === g && matches(e, needle)) })).filter((x) => x.items.length);
  const hits = !page && dq.length >= 2 ? (search.data ?? []) : [];
  const hitGroups = Object.keys(TYPE_GROUP).map((t) => ({ t, items: hits.filter((h) => h.type === t) })).filter((x) => x.items.length);
  const ask = !page && dq && me?.entitlement.activeModules.includes("ai") && me.permissions.includes("ai:use");
  const pageProjects = (projects.data ?? []).filter((p) => !needle || `${p.name} ${p.company?.name ?? ""}`.toLowerCase().includes(needle.toLowerCase()));
  const pageThemes = THEME_OPTIONS.filter((o) => !needle || o.label.toLowerCase().includes(needle.toLowerCase()));
  const nothing = page === "projects" ? !projects.isLoading && pageProjects.length === 0 : page === "theme" ? pageThemes.length === 0 : !ask && groups.length === 0 && hitGroups.length === 0 && !search.isFetching;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[55] bg-scrim/30 flex justify-center p-4 pt-[12vh]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
          <motion.div role="dialog" aria-modal="true" aria-label="Cerca" className="w-full max-w-[640px] bg-surface rounded-2xl border border-line shadow-2xl overflow-hidden h-fit" initial={{ y: -8, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ opacity: 0 }}>
            <Command
              resetKey={`${page ?? ""}|${needle}`}
              onKeyDown={(e) => {
                if (e.key === "Escape") (e.preventDefault(), page ? setPages((p) => p.slice(0, -1)) : onClose());
                // ⌫ con la ricerca vuota torna alla pagina precedente
                if (e.key === "Backspace" && !q && page) (e.preventDefault(), setPages((p) => p.slice(0, -1)));
              }}
            >
              <label htmlFor="palette" className="sr-only">
                Cerca in tutta la suite
              </label>
              <CommandInput
                id="palette"
                ref={input}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={page ? PAGES[page].placeholder : "Cerca o scrivi un comando…"}
                chip={page && <span className="h-6 px-2 rounded-md bg-hover text-xs font-medium text-text2 flex items-center shrink-0">{PAGES[page].label}</span>}
                trailing={search.isFetching || projects.isFetching ? <Spinner size={16} /> : null}
              />
              <CommandList>
                {nothing && <CommandEmpty>{!page && dq.length < 2 && dq ? "Scrivi almeno due lettere per cercare." : "Nessun risultato."}</CommandEmpty>}

                {page === "projects" && (
                  <CommandGroup heading="Progetti attivi">
                    {pageProjects.map((p) => (
                      <CommandItem key={p.id} value={`p-${p.id}`} icon="kanban" color={p.color} sub={p.company?.name} meta={p.overdue ? `${p.overdue} in ritardo` : undefined} onSelect={() => go(`/projects/${p.id}`)}>
                        {p.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {page === "theme" && (
                  <CommandGroup heading="Tema">
                    {pageThemes.map((o) => (
                      <CommandItem key={o.value} value={`t-${o.value}`} icon={o.icon} meta={theme === o.value ? "in uso" : undefined} onSelect={() => (setThemePref(o.value), onClose())}>
                        {o.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!page && (
                  <>
                    {hitGroups.map(({ t, items }, i) => (
                      <Fragment key={t}>
                        {i > 0 && <CommandSeparator />}
                        <CommandGroup heading={TYPE_GROUP[t]}>
                          {items.map((h) => (
                            <CommandItem key={`${h.type}:${h.id}`} value={`h-${h.type}-${h.id}`} icon={TYPE_ICON[h.type] ?? "circle"} color={moduleColor(h.module)} sub={h.subtitle ?? undefined} onSelect={() => go(h.link)}>
                              {h.title}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Fragment>
                    ))}
                    {groups.map(({ g, items }, i) => (
                      <Fragment key={g}>
                        {(i > 0 || hitGroups.length > 0) && <CommandSeparator />}
                        <CommandGroup heading={g}>
                          {items.map((e) => (
                            <CommandItem key={e.value} value={e.value} icon={e.icon} color={e.color} shortcut={e.shortcut} chevron={!!e.page} onSelect={() => select(e)}>
                              {e.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Fragment>
                    ))}
                    {/* in fondo: se nessun comando è quello giusto, si chiede a Kipo AI */}
                    {ask && (
                      <>
                        {(groups.length > 0 || hitGroups.length > 0) && <CommandSeparator />}
                      <CommandGroup heading="Kipo AI">
                        <CommandItem value="ask" icon="sparkles" onSelect={() => select({ value: "ask", group: "", label: "", icon: "sparkles", to: "/assistant", search: { q: dq } })}>
                          Chiedi a Kipo AI: “{dq}”
                        </CommandItem>
                      </CommandGroup>
                      </>
                    )}
                  </>
                )}
              </CommandList>
              <div className="flex items-center gap-4 px-4 h-10 border-t border-line text-xs text-muted shrink-0">
                <span className="flex items-center gap-1.5"><CommandShortcut keys="↑ ↓" /> per muoverti</span>
                <span className="flex items-center gap-1.5"><CommandShortcut keys="↵" /> per aprire</span>
                {page && <span className="flex items-center gap-1.5"><CommandShortcut keys="⌫" /> per tornare indietro</span>}
                <span className="flex items-center gap-1.5 ml-auto"><CommandShortcut keys="esc" /> {page ? "indietro" : "per chiudere"}</span>
              </div>
            </Command>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
